import React, { createContext, useContext, useState, useEffect } from 'react';

// Translations object
const translations = {
  en: {
    // Header
    home: 'Home',
    assessment: 'Quickscan',
    doQuickscan: 'Do the quickscan',
    useCases: 'Use Cases',
    myDashboard: 'My Dashboard',
    login: 'Login',
    logout: 'Logout',
    
    // Hero Section
    heroTitle: 'How AI-ready is your HR team?',
    heroSubtitle: 'Take our free quickscan in just 4 minutes to measure your HR AI readiness. Get immediate insights into opportunities, risks and concrete next steps. Compare your organization with 200+ HR teams across industries.',
    startFreeAssessment: 'Start free quickscan',
    exploreUseCases: 'Explore free use cases',
    free: '100% free',
    realTimeBenchmark: 'Real-time benchmark',
    aiPoweredInsights: 'AI-powered insights',
    fourMinutes: '4 minutes',
    
    // Assessment Preview
    seeHowItWorks: 'See how it works',
    experienceAssessment: 'Experience the quickscan',
    experienceSubtitle: 'Answer questions across 4 dimensions in just 4 minutes. Get instant insights into your HR AI readiness.',
    assessmentTab: 'Quickscan',
    resultsTab: 'Results',
    assessmentProgress: 'Quickscan progress',
    yourAIMaturityProfile: 'Your AI for HR profile',
    overallScore: 'Overall score',
    startYourFreeAssessment: 'Start your free quickscan',
    takesOnly: 'Takes only 4 minutes • 100% free • Instant results',
    pdfPreview: 'PDF report preview',
    downloadSample: 'Download sample report',
    
    // Assessment Form
    freeAIAssessment: 'Free AI for HR maturity quickscan',
    poweredBy: 'Powered by Humanistix',
    companyName: 'Company name',
    industry: 'Industry',
    companySize: 'Company size',
    yourName: 'Your name',
    email: 'Email',
    phoneNumber: 'Phone number',
    jobTitle: 'Job title',
    assessmentInfo: 'This free quickscan takes approximately 4 minutes. You will receive your results and benchmarking against your sector directly.',
    startQuestions: 'Start questions',
    loggedIn: 'Logged in',
    select: 'Select...',
    optional: 'optional',
    betaCode: 'Beta Code',
    betaCodeHint: 'Enter a beta code to unlock beta tester features',
    
    // Assessment Questions
    sectionOf: 'Section {current} of {total}',
    complete: '% complete',
    currentScore: 'Current score',
    previous: 'Previous',
    nextSection: 'Next Section',
    seeResults: 'See Results',
    submitting: 'Submitting...',
    
    // Score Labels
    notAtAll: 'Not at all',
    limited: 'Limited',
    average: 'Average',
    greatExtent: 'Great extent',
    completely: 'Completely',
    
    // Maturity Levels
    beginning: 'Beginning',
    emerging: 'Emerging',
    developing: 'Developing',
    advanced: 'Advanced',
    leading: 'Leading',
    
    // Dimensions (4 categories)
    strategy: 'Strategy',
    governance: 'Governance',
    technologyData: 'Technology & Data',
    peopleChange: 'People & Change',
    // Legacy dimensions (for backwards compatibility)
    technology: 'Technology',
    people: 'People',
    skills: 'Skills',
    data: 'Data',
    
    // Results Page
    assessmentComplete: 'Your AI for HR Quickscan is complete!',
    thanksForCompleting: 'Thank you for completing the quickscan. Here are your results and recommendations.',
    yourResults: 'Your AI for HR maturity results',
    aiMaturityScore: 'AI for HR maturity score',
    industryBenchmark: 'Industry benchmark',
    basedOnAssessments: 'Based on {count}+ free quickscans',
    vsBenchmark: 'vs. benchmark',
    aboveAverage: '+{diff}% above average',
    belowAverage: '{diff}% below average',
    atAverage: 'At industry average',
    howYouCompare: 'How you compare',
    maturityOverview: 'AI for HR maturity overview',
    visualRepresentation: 'Visual representation of your scores per dimension',
    scoreBreakdown: 'Score breakdown',
    totalScore: 'Total score',
    scorePerDimension: 'Score per dimension',
    aiGeneratedReport: 'AI-generated strategy report',
    executiveSummary: 'Executive summary',
    urgentAction: 'Urgent action recommended',
    keyStrengths: 'Key strengths',
    recommendedActions: 'Recommended actions',
    freeNextSteps: 'Free next steps',
    receiveFreeReport: 'Receive your FREE AI for HR strategy report',
    downloadDescription: 'Download your free personalized report with detailed insights, benchmarks, and action plan.',
    downloadPdfReport: 'Download PDF report',
    generatingPdf: 'Generating PDF...',
    backToHome: 'Back to home',
    exploreUseCasesBtn: 'Explore use cases',
    loginToUnlock: 'Log in to unlock use cases',
    
    // What We Measure
    whatWeMeasure: 'What we measure',
    fourDimensions: '4 dimensions that determine your AI readiness',
    strategyDesc: 'Strategic alignment and vision for AI adoption within HR',
    governanceDesc: 'Policies, ethics, and risk management for responsible AI use',
    technologyData: 'Technology & data',
    technologyDataDesc: 'Technical infrastructure, data quality, and tools to support AI implementation',
    peopleChange: 'People & change',
    peopleChangeDesc: 'Employee readiness, AI skills development, and change management for successful adoption',
    
    // Footer
    solutions: 'Solutions',
    company: 'Company',
    resources: 'Resources',
    freeAIAssessmentFooter: 'Free AI quickscan',
    freeUseCases: 'Free use cases',
    freeWorkshops: 'Workshops',
    consulting: 'Consulting',
    aboutUs: 'About us',
    team: 'Team',
    careers: 'Careers',
    contact: 'Contact',
    blog: 'Blog',
    caseStudies: 'Case studies',
    documentation: 'Documentation',
    faq: 'FAQ',
    footerTagline: 'Empowering HR teams with free AI-driven insights and practical solutions for the future of work.',
    allRightsReserved: 'All rights reserved.',
    privacyPolicy: 'Privacy policy',
    termsOfService: 'Terms of service',
    cookiePolicy: 'Cookie policy',
    
    // CTA Section
    readyToDiscover: 'Ready to discover your AI potential?',
    ctaDescription: 'Join 200+ HR teams who have already completed their quickscan. Get personalized recommendations and start your transformation journey today.',
    takeFreeAssessment: 'Take the free quickscan',
    
    // Stats
    assessmentsCompleted: 'Quickscans completed',
    avgScore: 'Average score',
    useCasesAvailable: 'Use cases available',
    satisfaction: 'Satisfaction',
    
    // Testimonials
    whatOurUsersSay: 'What our users say',
    trustedByTeams: 'Trusted by HR teams across industries',
    
    // Use Cases
    useCasesHub: 'AI use cases hub',
    useCasesDescription: 'Discover practical AI applications for HR. Filter by category, complexity, or impact to find the right solutions for your organization.',
    searchPlaceholder: 'Search use cases...',
    allCategories: 'All categories',
    savedByPeople: 'Saved by {count} people',
    
    // ROI
    roiCalculator: 'ROI calculator',
    roiLocked: 'ROI calculator access required',
    roiLockedDescription: 'The ROI calculator is a premium feature. Please schedule a consultation to unlock access.',
    scheduleConsultation: 'Schedule a consultation',
    
    // Login
    welcomeBack: 'Welcome back',
    loginDescription: 'Sign in to access your dashboard and saved use cases.',
    password: 'Password',
    signIn: 'Sign in',
    orContinueWith: 'Or continue with',
    continueWithGoogle: 'Continue with Google',
    noAccount: "Don't have an account?",
    signUp: 'Sign up',
    
    // Register
    createAccount: 'Create account',
    registerDescription: 'Join thousands of HR professionals improving their AI readiness.',
    fullName: 'Full name',
    createAccountBtn: 'Create account',
    alreadyHaveAccount: 'Already have an account?',
    
    // Dashboard
    welcomeUser: 'Welcome back, {name}!',
    dashboardDescription: "Here's an overview of your AI for HR journey.",
    myAssessments: 'My quickscans',
    savedUseCases: 'Saved use cases',
    latestScore: 'Latest score',
    takeNewAssessment: 'Take new quickscan',
    noAssessmentsYet: 'No quickscans yet',
    startFirstAssessment: 'Take your first quickscan to start tracking your AI for HR progress.',
    viewResults: 'View results',
    noSavedUseCases: 'No saved use cases yet',
    exploreAndSave: 'Explore use cases and save your favorites for quick access.',
    browseUseCases: 'Browse use cases',
    
    // Assessment Questions - Dimensions
    strategyTitle: 'Strategy',
    strategyDescription: 'Strategic alignment and vision for AI adoption within HR',
    governanceTitle: 'Governance',
    governanceDescription: 'Policies, ethics, and risk management for responsible AI use',
    technologyTitle: 'Technology',
    technologyDescription: 'Technical infrastructure and tools to support AI implementation',
    peopleTitle: 'People',
    peopleDescription: 'Employee and leadership readiness and acceptance of AI',
    skillsTitle: 'Skills',
    skillsDescription: 'AI competencies and development opportunities for HR teams',
    dataTitle: 'Data',
    dataDescription: 'Data quality, accessibility, and management for AI success',
    
    // Industries
    industryTechnology: 'Technology',
    industryFinance: 'Financial services',
    industryHealthcare: 'Healthcare',
    industryManufacturing: 'Manufacturing',
    industryRetail: 'Retail',
    industryEducation: 'Education',
    industryGovernment: 'Government',
    industryConsulting: 'Consulting',
    industryOther: 'Other',
    
    // Company Sizes
    size1_50: '1-50 employees',
    size51_200: '51-200 employees',
    size201_500: '201-500 employees',
    size501_1000: '501-1000 employees',
    size1001_5000: '1001-5000 employees',
    size5000plus: '5000+ employees',
    
    // Results Page Additional
    yourOrganization: "Your organization's position",
    indicativeVisualization: 'Indicative visualization; scores are calculated based on quickscan answers',
    avg: 'avg',
    yourScore: 'Your score',
    industryAvg: 'Industry avg',
    startWithQuickWins: 'Start with Quick Wins',
    quickWinsDesc: 'Implement low-complexity, high-impact AI use cases like CV screening or FAQ chatbots',
    improveDimension: 'Improve {dimension}',
    improveDimensionDesc: 'Focus on building capabilities in your weakest area to create a balanced AI foundation',
    buildGovernance: 'Build AI Governance Framework',
    buildGovernanceDesc: 'Establish policies for data usage, bias monitoring, and ethical AI deployment',
    upskillTeam: 'Upskill Your Team',
    upskillTeamDesc: 'Invest in AI literacy and practical skills training for HR professionals',
    bookConsultation: 'Book a FREE consultation to discuss your personalized roadmap.',
    exploreUseCaseHub: 'Explore our FREE use case hub for relevant AI applications.',
    joinWorkshop: 'Join our AI in HR workshops for hands-on experience.',
    high: 'High',
    medium: 'Medium',
    urgent: 'Urgent',
    
    // Login/Register Additional
    termsAgreement: 'By continuing, you agree to our Terms of Service and Privacy Policy.',
    nameRequired: 'Name is required',
    
    // Dashboard Additional
    viewYourAIJourney: "Here's an overview of your AI for HR journey.",
    yourAIMaturityProgress: 'Your AI for HR maturity progress',
    assessmentsCompleted2: 'quickscan(s) completed',
    vsPrevious: 'vs. previous',
    scoreProgression: 'Score progression',
    dimensionComparison: 'Dimension comparison (latest vs. previous)',
    latest: 'Latest',
    previousLabel: 'Previous',
    startSecondAssessment: 'Start your second quickscan',
    afterSecondAssessment: 'After your second quickscan, you will see your progress over time here.',
    startNewAssessment: 'Start new quickscan',
    myAssessmentsTitle: 'My quickscans',
    newAssessment: 'New quickscan',
    noAssessmentsYetTitle: 'No quickscans yet',
    startFirstAssessmentDesc: 'Start your first free AI for HR maturity quickscan to benchmark your organization.',
    startFreeAssessmentBtn: 'Start free quickscan',
    latestLabel: 'Latest',
    mySavedUseCases: 'My saved use cases',
    browseFreeUseCases: 'Browse free use cases',
    noSavedUseCasesTitle: 'No saved use cases yet',
    saveInterestingUseCases: 'Save interesting AI use cases to find them later.',
    discoverFreeUseCases: 'Discover free use cases',
    startFreeAssessmentCard: 'Start free quickscan',
    discoverYourScore: 'Discover your AI for HR maturity score',
    startNow: 'Start now',
    freeUseCaseHub: 'Free use case hub',
    discoverAIApplications: 'Discover AI applications for HR',
    viewUseCasesLink: 'View use cases',
    roiComparer: 'ROI comparer',
    compareAIUseCases: 'Compare AI use cases by ROI',
    compareNow: 'Compare now',
    impact: 'impact',
    removeFromSavedList: 'Remove from saved list',
    
    // Use Case Detail Page
    loadingUseCase: 'Loading use case...',
    useCaseNotFound: 'Use case not found',
    backToUseCases: 'Back to use cases',
    removeFromSaved: 'Remove from saved',
    saveToList: 'Save to your list',
    saved: 'saved',
    clickToRemoveVote: 'Click to remove your vote',
    clickToVote: 'Click to vote',
    votes: 'votes',
    implementationSteps: 'Implementation steps',
    exampleTools: 'Example tools',
    expectedOutcomes: 'Expected outcomes',
    quickInfo: 'Quick info',
    implementationTime: 'Implementation time',
    domainLabel: 'Domain',
    businessImpact: 'Business impact',
    readyToImplement: 'Ready to implement?',
    calculateROI: 'Calculate ROI',
    bookWorkshop: 'Book workshop',
    
    // Registration Benefits Section
    whyRegister: 'Why register?',
    whyRegisterSubtitle: 'Create a free account and get access to extra features',
    registerBenefit1Title: 'Save use cases',
    registerBenefit1Desc: 'Save interesting AI use cases and review them later in your personal dashboard.',
    registerBenefit2Title: 'Track your progress',
    registerBenefit2Desc: 'Take multiple quickscans and track your AI for HR score over time.',
    registerBenefit3Title: 'Personal dashboard',
    registerBenefit3Desc: 'Get access to a clear dashboard with all your quickscans and saved items.',
    registerBenefit4Title: 'Exclusive content',
    registerBenefit4Desc: 'Get access to in-depth use case details and implementation guides.',
    useCasesRequireLogin: 'To view and save use cases, you need to register first.',
    createFreeAccount: 'Create free account',
  },
  nl: {
    // Header
    home: 'Home',
    assessment: 'Quickscan',
    doQuickscan: 'Doe de quickscan',
    useCases: 'AI Toepassingen',
    myDashboard: 'Mijn dashboard',
    login: 'Inloggen',
    logout: 'Uitloggen',
    
    // Hero Section
    heroTitle: 'Hoe AI-ready is uw HR-team?',
    heroSubtitle: 'Doe onze gratis quickscan in slechts 4 minuten om uw HR AI-gereedheid te meten. Krijg onmiddellijk inzicht in kansen, risico\'s en concrete vervolgstappen. Vergelijk uw organisatie met 200+ HR-teams uit diverse sectoren.',
    startFreeAssessment: 'Start gratis quickscan',
    exploreUseCases: 'Bekijk gratis use cases',
    free: '100% gratis',
    realTimeBenchmark: 'Realtime benchmark',
    aiPoweredInsights: 'AI-gestuurde inzichten',
    fourMinutes: '4 minuten',
    
    // Assessment Preview
    seeHowItWorks: 'Bekijk hoe het werkt',
    experienceAssessment: 'Ervaar de quickscan',
    experienceSubtitle: 'Beantwoord vragen in 4 dimensies in slechts 4 minuten. Krijg direct inzicht in uw HR AI-gereedheid.',
    assessmentTab: 'Quickscan',
    resultsTab: 'Resultaten',
    assessmentProgress: 'Quickscan voortgang',
    yourAIMaturityProfile: 'Uw AI for HR profiel',
    overallScore: 'Totaalscore',
    startYourFreeAssessment: 'Start uw gratis quickscan',
    takesOnly: 'Duurt slechts 4 minuten • 100% gratis • Direct resultaat',
    pdfPreview: 'PDF-rapport preview',
    downloadSample: 'Download voorbeeldrapport',
    
    // Assessment Form
    freeAIAssessment: 'Gratis AI voor HR maturity quickscan',
    poweredBy: 'Powered by Humanistix',
    companyName: 'Bedrijfsnaam',
    industry: 'Sector',
    companySize: 'Bedrijfsgrootte',
    yourName: 'Uw naam',
    email: 'E-mail',
    phoneNumber: 'Telefoonnummer',
    jobTitle: 'Functie',
    assessmentInfo: 'Deze gratis quickscan duurt ongeveer 4 minuten. U ontvangt direct uw resultaten en benchmarking tegen uw sector.',
    startQuestions: 'Start vragen',
    loggedIn: 'Ingelogd',
    select: 'Selecteer...',
    optional: 'optioneel',
    betaCode: 'Betacode',
    betaCodeHint: 'Voer een betacode in om betatesterfuncties te ontgrendelen',
    
    // Assessment Questions
    sectionOf: 'Sectie {current} van {total}',
    complete: '% voltooid',
    currentScore: 'Huidige score',
    previous: 'Vorige',
    nextSection: 'Volgende sectie',
    seeResults: 'Bekijk resultaten',
    submitting: 'Verzenden...',
    
    // Score Labels
    notAtAll: 'Helemaal niet',
    limited: 'Beperkt',
    average: 'Gemiddeld',
    greatExtent: 'Grote mate',
    completely: 'Volledig',
    
    // Maturity Levels
    beginning: 'Begin',
    emerging: 'Opkomend',
    developing: 'Ontwikkelend',
    advanced: 'Gevorderd',
    leading: 'Leidend',
    
    // Dimensions (4 categories)
    strategy: 'Strategie',
    governance: 'Governance',
    technologyData: 'Technologie & data',
    peopleChange: 'Mensen & change',
    // Legacy dimensions (for backwards compatibility)
    technology: 'Technologie',
    people: 'Mensen',
    skills: 'Vaardigheden',
    data: 'Data',
    
    // Results Page
    assessmentComplete: 'Uw AI for HR Quickscan is voltooid!',
    thanksForCompleting: 'Bedankt voor het voltooien van de quickscan. Hier zijn uw resultaten en aanbevelingen.',
    yourResults: 'Uw AI voor HR maturity resultaten',
    aiMaturityScore: 'AI voor HR maturity score',
    industryBenchmark: 'Sector benchmark',
    basedOnAssessments: 'Gebaseerd op {count}+ gratis quickscans',
    vsBenchmark: 'vs. benchmark',
    aboveAverage: '+{diff}% boven gemiddelde',
    belowAverage: '{diff}% onder gemiddelde',
    atAverage: 'Op sectorgemiddelde',
    howYouCompare: 'Hoe u scoort',
    maturityOverview: 'AI voor HR maturity overzicht',
    visualRepresentation: 'Visuele weergave van uw scores per dimensie',
    scoreBreakdown: 'Score-overzicht',
    totalScore: 'Totaalscore',
    scorePerDimension: 'Score per dimensie',
    aiGeneratedReport: 'AI-gegenereerd strategierapport',
    executiveSummary: 'Samenvatting',
    urgentAction: 'Urgente actie aanbevolen',
    keyStrengths: 'Belangrijkste sterktes',
    recommendedActions: 'Aanbevolen acties',
    freeNextSteps: 'Gratis vervolgstappen',
    receiveFreeReport: 'Ontvang uw GRATIS AI voor HR strategierapport',
    downloadDescription: 'Download uw gratis gepersonaliseerd rapport met gedetailleerde inzichten, benchmarks en actieplan.',
    downloadPdfReport: 'Download PDF-rapport',
    generatingPdf: 'PDF genereren...',
    backToHome: 'Terug naar home',
    exploreUseCasesBtn: 'Bekijk use cases',
    loginToUnlock: 'Log in om use cases te ontgrendelen',
    
    // What We Measure
    whatWeMeasure: 'Wat we meten',
    fourDimensions: '4 dimensies die uw AI-gereedheid bepalen',
    strategyDesc: 'Strategische afstemming en visie voor AI-adoptie binnen HR',
    governanceDesc: 'Beleid, ethiek en risicobeheer voor verantwoord AI-gebruik',
    technologyData: 'Technologie & data',
    technologyDataDesc: 'Technische infrastructuur, datakwaliteit en tools om AI-implementatie te ondersteunen',
    peopleChange: 'Mensen & change',
    peopleChangeDesc: 'Medewerkersgereedheid, AI-vaardigheidsontwikkeling en verandermanagement voor succesvolle adoptie',
    
    // Footer
    solutions: 'Oplossingen',
    company: 'Bedrijf',
    resources: 'Bronnen',
    freeAIAssessmentFooter: 'Gratis AI-quickscan',
    freeUseCases: 'Gratis use cases',
    freeWorkshops: 'Workshops',
    consulting: 'Consultancy',
    aboutUs: 'Over ons',
    team: 'Team',
    careers: 'Carrières',
    contact: 'Contact',
    blog: 'Blog',
    caseStudies: 'Case studies',
    documentation: 'Documentatie',
    faq: 'FAQ',
    footerTagline: 'HR-teams versterken met gratis AI-gestuurde inzichten en praktische oplossingen voor de toekomst van werk.',
    allRightsReserved: 'Alle rechten voorbehouden.',
    privacyPolicy: 'Privacybeleid',
    termsOfService: 'Servicevoorwaarden',
    cookiePolicy: 'Cookiebeleid',
    
    // CTA Section
    readyToDiscover: 'Klaar om uw AI-potentieel te ontdekken?',
    ctaDescription: 'Sluit u aan bij 200+ HR-teams die hun quickscan al hebben voltooid. Krijg gepersonaliseerde aanbevelingen en start vandaag nog uw transformatie.',
    takeFreeAssessment: 'Doe de gratis quickscan',
    
    // Stats
    assessmentsCompleted: 'Quickscans voltooid',
    avgScore: 'Gemiddelde score',
    useCasesAvailable: 'Use cases beschikbaar',
    satisfaction: 'Tevredenheid',
    
    // Testimonials
    whatOurUsersSay: 'Wat onze gebruikers zeggen',
    trustedByTeams: 'Vertrouwd door HR-teams in alle sectoren',
    
    // Use Cases
    useCasesHub: 'AI use cases hub',
    useCasesDescription: 'Ontdek praktische AI-toepassingen voor HR. Filter op categorie, complexiteit of impact om de juiste oplossingen voor uw organisatie te vinden.',
    searchPlaceholder: 'Zoek use cases...',
    allCategories: 'Alle categorieën',
    savedByPeople: 'Bewaard door {count} mensen',
    
    // ROI
    roiCalculator: 'ROI-calculator',
    roiLocked: 'ROI-calculator toegang vereist',
    roiLockedDescription: 'De ROI-calculator is een premium functie. Plan een consultatie om toegang te krijgen.',
    scheduleConsultation: 'Plan een consultatie',
    
    // Login
    welcomeBack: 'Welkom terug',
    loginDescription: 'Log in om toegang te krijgen tot uw dashboard en opgeslagen use cases.',
    password: 'Wachtwoord',
    signIn: 'Inloggen',
    orContinueWith: 'Of ga verder met',
    continueWithGoogle: 'Ga verder met Google',
    noAccount: 'Nog geen account?',
    signUp: 'Registreer',
    
    // Register
    createAccount: 'Account aanmaken',
    registerDescription: 'Sluit u aan bij duizenden HR-professionals die hun AI-gereedheid verbeteren.',
    fullName: 'Volledige naam',
    createAccountBtn: 'Account aanmaken',
    alreadyHaveAccount: 'Heeft u al een account?',
    
    // Dashboard
    welcomeUser: 'Welkom terug, {name}!',
    dashboardDescription: 'Hier is een overzicht van uw AI for HR reis.',
    myAssessments: 'Mijn quickscans',
    savedUseCases: 'Opgeslagen use cases',
    latestScore: 'Laatste score',
    takeNewAssessment: 'Nieuwe quickscan',
    noAssessmentsYet: 'Nog geen quickscans',
    startFirstAssessment: 'Doe uw eerste quickscan om uw AI for HR voortgang te volgen.',
    viewResults: 'Bekijk resultaten',
    noSavedUseCases: 'Nog geen opgeslagen use cases',
    exploreAndSave: 'Verken use cases en bewaar uw favorieten voor snelle toegang.',
    browseUseCases: 'Bekijk use cases',
    
    // Assessment Questions - Dimensions
    strategyTitle: 'Strategie',
    strategyDescription: 'Strategische afstemming en visie voor AI-adoptie binnen HR',
    governanceTitle: 'Governance',
    governanceDescription: 'Beleid, ethiek en risicobeheer voor verantwoord AI-gebruik',
    technologyTitle: 'Technologie',
    technologyDescription: 'Technische infrastructuur en tools om AI-implementatie te ondersteunen',
    peopleTitle: 'Mensen',
    peopleDescription: 'Medewerker- en leiderschapsgereedheid en acceptatie van AI',
    skillsTitle: 'Vaardigheden',
    skillsDescription: 'AI-competenties en ontwikkelingsmogelijkheden voor HR-teams',
    dataTitle: 'Data',
    dataDescription: 'Datakwaliteit, toegankelijkheid en beheer voor AI-succes',
    
    // Industries
    industryTechnology: 'Technologie',
    industryFinance: 'Financiële diensten',
    industryHealthcare: 'Gezondheidszorg',
    industryManufacturing: 'Productie',
    industryRetail: 'Retail',
    industryEducation: 'Onderwijs',
    industryGovernment: 'Overheid',
    industryConsulting: 'Consultancy',
    industryOther: 'Anders',
    
    // Company Sizes
    size1_50: '1-50 medewerkers',
    size51_200: '51-200 medewerkers',
    size201_500: '201-500 medewerkers',
    size501_1000: '501-1000 medewerkers',
    size1001_5000: '1001-5000 medewerkers',
    size5000plus: '5000+ medewerkers',
    
    // Results Page Additional
    yourOrganization: 'De positie van uw organisatie',
    indicativeVisualization: 'Indicatieve visualisatie; scores worden berekend op basis van quickscan antwoorden',
    avg: 'gem',
    yourScore: 'Uw score',
    industryAvg: 'Sector gem.',
    startWithQuickWins: 'Begin met quick wins',
    quickWinsDesc: 'Implementeer laag-complexe, hoge-impact AI use cases zoals CV-screening of FAQ-chatbots',
    improveDimension: 'Verbeter {dimension}',
    improveDimensionDesc: 'Focus op het opbouwen van capaciteiten in uw zwakste gebied om een gebalanceerde AI-basis te creëren',
    buildGovernance: 'Bouw AI governance framework',
    buildGovernanceDesc: 'Stel beleid op voor datagebruik, bias-monitoring en ethische AI-implementatie',
    upskillTeam: 'Train uw team',
    upskillTeamDesc: 'Investeer in AI-geletterdheid en praktische vaardigheden training voor HR-professionals',
    bookConsultation: 'Boek een GRATIS consultatie om uw gepersonaliseerde roadmap te bespreken.',
    exploreUseCaseHub: 'Verken onze GRATIS use case hub voor relevante AI-toepassingen.',
    joinWorkshop: 'Neem deel aan onze AI in HR workshops voor praktijkervaring.',
    high: 'Hoog',
    medium: 'Gemiddeld',
    urgent: 'Urgent',
    
    // Login/Register Additional
    termsAgreement: 'Door verder te gaan, gaat u akkoord met onze servicevoorwaarden en privacybeleid.',
    nameRequired: 'Naam is verplicht',
    
    // Dashboard Additional
    viewYourAIJourney: 'Hier is een overzicht van uw AI for HR reis.',
    yourAIMaturityProgress: 'Uw AI voor HR maturity progressie',
    assessmentsCompleted2: 'quickscan(s) voltooid',
    vsPrevious: 'vs. vorige',
    scoreProgression: 'Score progressie',
    dimensionComparison: 'Dimensie vergelijking (laatste vs. vorige)',
    latest: 'Laatste',
    previousLabel: 'Vorige',
    startSecondAssessment: 'Start uw tweede quickscan',
    afterSecondAssessment: 'Na uw tweede quickscan ziet u hier uw progressie over tijd.',
    startNewAssessment: 'Start nieuwe quickscan',
    myAssessmentsTitle: 'Mijn quickscans',
    newAssessment: 'Nieuwe quickscan',
    noAssessmentsYetTitle: 'Nog geen quickscans',
    startFirstAssessmentDesc: 'Doe uw eerste gratis AI voor HR maturity quickscan om uw organisatie te benchmarken.',
    startFreeAssessmentBtn: 'Start gratis quickscan',
    latestLabel: 'Nieuwste',
    mySavedUseCases: 'Mijn opgeslagen use cases',
    browseFreeUseCases: 'Bekijk gratis use cases',
    noSavedUseCasesTitle: 'Nog geen opgeslagen use cases',
    saveInterestingUseCases: 'Bewaar interessante AI use cases om ze later terug te vinden.',
    discoverFreeUseCases: 'Ontdek gratis use cases',
    startFreeAssessmentCard: 'Start gratis quickscan',
    discoverYourScore: 'Ontdek uw AI voor HR maturity score',
    startNow: 'Start nu',
    freeUseCaseHub: 'Gratis use case hub',
    discoverAIApplications: 'Ontdek AI-toepassingen voor HR',
    viewUseCasesLink: 'Bekijk use cases',
    roiComparer: 'ROI-vergelijker',
    compareAIUseCases: 'Vergelijk AI use cases op ROI',
    compareNow: 'Vergelijk nu',
    impact: 'impact',
    removeFromSavedList: 'Verwijder uit opgeslagen lijst',
    
    // Use Case Detail Page
    loadingUseCase: 'Use case laden...',
    useCaseNotFound: 'Use case niet gevonden',
    backToUseCases: 'Terug naar use cases',
    removeFromSaved: 'Verwijder uit opgeslagen',
    saveToList: 'Opslaan in uw lijst',
    saved: 'opgeslagen',
    clickToRemoveVote: 'Klik om uw stem te verwijderen',
    clickToVote: 'Klik om te stemmen',
    votes: 'stemmen',
    implementationSteps: 'Implementatiestappen',
    exampleTools: 'Voorbeeldtools',
    expectedOutcomes: 'Verwachte resultaten',
    quickInfo: 'Snelle info',
    implementationTime: 'Implementatietijd',
    domainLabel: 'Domein',
    businessImpact: 'Bedrijfsimpact',
    readyToImplement: 'Klaar om te implementeren?',
    calculateROI: 'Bereken ROI',
    bookWorkshop: 'Boek workshop',
    
    // Registration Benefits Section
    whyRegister: 'Waarom registreren?',
    whyRegisterSubtitle: 'Maak een gratis account aan en krijg toegang tot extra functionaliteiten',
    registerBenefit1Title: 'Bewaar use cases',
    registerBenefit1Desc: 'Sla interessante AI use cases op en bekijk ze later terug in uw persoonlijke dashboard.',
    registerBenefit2Title: 'Volg uw voortgang',
    registerBenefit2Desc: 'Doe meerdere quickscans en volg uw AI for HR score over tijd.',
    registerBenefit3Title: 'Persoonlijk dashboard',
    registerBenefit3Desc: 'Krijg toegang tot een overzichtelijk dashboard met al uw quickscans en opgeslagen items.',
    registerBenefit4Title: 'Exclusieve content',
    registerBenefit4Desc: 'Krijg toegang tot diepgaande use case details en implementatiegidsen.',
    useCasesRequireLogin: 'Om use cases te bekijken en op te slaan, moet u eerst registreren.',
    createFreeAccount: 'Maak gratis account aan',
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first, then browser preference
    const saved = localStorage.getItem('language');
    if (saved) return saved;
    
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('nl')) return 'nl';
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key, params = {}) => {
    let text = translations[language]?.[key] || translations['en']?.[key] || key;
    
    // Replace parameters like {name} with actual values
    Object.keys(params).forEach(param => {
      text = text.replace(`{${param}}`, params[param]);
    });
    
    return text;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'nl' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
